import React, { useState } from 'react';
import { X, Loader, Upload, FileText, CheckCircle } from 'lucide-react';
import CameraCapture from './CameraCapture';
import { fileToUpload, dataUrlToUpload } from '../utils/docUpload';

// Shared "upload a document" modal for the contractor portal. Handles a photo /
// camera capture (CameraCapture, side-button shutter via nativeFirst) AND a PDF /
// file pick, normalises to base64, and posts driver-docs-sb `uploadMyDoc`.
//   • requestId set  → fulfils that office request
//   • requestId null → a free self-upload (the driver types a title)
const API = '/.netlify/functions/driver-docs-sb';

export default function DocUploadModal({ requestId = null, requestTitle = '', onClose, onDone }) {
  const [photo, setPhoto]   = useState(null);   // CameraCapture data URL
  const [file, setFile]     = useState(null);   // { base64, contentType, filename } from a file pick
  const [fileName, setFileName] = useState('');
  const [title, setTitle]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');

  const onPickFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    setError('');
    try { const u = await fileToUpload(f); setFile(u); setFileName(u.filename); setPhoto(null); }
    catch (err) { setError(err.message); }
  };

  const submit = async () => {
    setError('');
    let payload;
    try {
      if (file) payload = file;
      else if (photo) payload = dataUrlToUpload(photo, 'photo.jpg');
      else { setError('Please take a photo or choose a file first.'); return; }
    } catch (err) { setError(err.message); return; }

    if (!requestId && !title.trim()) { setError('Please give the document a short name.'); return; }

    setBusy(true);
    try {
      const res = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'uploadMyDoc', requestId: requestId || undefined,
          title: (title.trim() || requestTitle || undefined),
          fileBase64: payload.base64, contentType: payload.contentType, filename: payload.filename,
        }),
      }).then(r => r.json());
      if (!res || !res.success) { setError(res?.error || 'Upload failed. Please try again.'); setBusy(false); return; }
      onDone && onDone();
    } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1300, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ width: 520, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{requestId ? 'Upload requested document' : 'Upload a document'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}><X size={16} /></button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {requestId ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
              The office asked for: <b style={{ color: 'var(--hp-text)' }}>{requestTitle}</b>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'block', marginBottom: 5 }}>What is this document?</label>
              <input
                type="text" placeholder="e.g. Police check, Insurance certificate…" value={title}
                onChange={e => setTitle(e.target.value)}
                style={{ width: '100%', padding: '11px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
          )}

          {/* Photo / camera capture (side-button shutter via nativeFirst) */}
          {!file && (
            <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.8}
              value={photo} onCapture={(u) => { setPhoto(u); setFile(null); setFileName(''); }} onClear={() => setPhoto(null)} />
          )}

          {/* PDF / file pick */}
          {!photo && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start', padding: '10px 14px', border: '1px dashed var(--hp-border)', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--hp-text)' }}>
              {file ? <CheckCircle size={15} color="var(--hp-green)" /> : <FileText size={15} color="var(--hp-gold)" />}
              {file ? `Attached: ${fileName}` : 'Attach a PDF or file'}
              <input type="file" accept="application/pdf,image/*" onChange={onPickFile} style={{ display: 'none' }} />
            </label>
          )}
          {file && <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => { setFile(null); setFileName(''); }}>Choose a different file</button>}

          {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.82rem' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={busy || (!photo && !file)} onClick={submit}>
            {busy ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</> : <><Upload size={14} /> Send to office</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
